"use client";

import type { IBlogHeading } from "@/types/blog";
import { cn } from "@/lib/utils";

export interface ITableOfContentsProps {
  headings: IBlogHeading[];
  className?: string;
}

/** Builds hierarchical section numbers: H2 → x., H3 → x.y., H4 → x.y.z. */
function buildHeadingNumbers(headings: IBlogHeading[]): string[] {
  let h2 = 0;
  let h3 = 0;
  let h4 = 0;

  return headings.map((heading) => {
    switch (heading.level) {
      case 1:
      case 2:
        h2 += 1;
        h3 = 0;
        h4 = 0;
        return `${h2}.`;
      case 3:
        if (h2 === 0) h2 = 1;
        h3 += 1;
        h4 = 0;
        return `${h2}.${h3}.`;
      case 4:
        if (h2 === 0) h2 = 1;
        if (h3 === 0) h3 = 1;
        h4 += 1;
        return `${h2}.${h3}.${h4}.`;
      default:
        return "";
    }
  });
}

function headingIndentPx(level: IBlogHeading["level"]): number {
  if (level <= 2) return 0;
  return (level - 2) * 12;
}

export function TableOfContents({
  headings,
  className,
}: ITableOfContentsProps): React.ReactElement | null {
  if (headings.length === 0) return null;

  const headingNumbers = buildHeadingNumbers(headings);

  return (
    <nav
      aria-label="Table of contents"
      className={cn(
        "border border-border rounded-lg p-2 hidden lg:block",
        className
      )}
    >
      <p className="text-sm font-semibold text-foreground">On this page</p>
      <ol className="mt-3 space-y-2 text-xs">
        {headings.map((heading, index) => (
          <li
            key={heading.id}
            style={{ paddingLeft: `${headingIndentPx(heading.level)}px` }}
          >
            <a
              href={`#${heading.id}`}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <span className="mr-1 tabular-nums text-primary/80">
                {headingNumbers[index]}
              </span>
              {heading.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
